import React from 'react';
import { Stack, CircularProgress, Typography } from '@mui/material';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { PERMISSIONS } from '../../../constants/permissions';
import { DashboardService } from '../../../services/dashboard.service';
import { ExpenseSummary, MonthlyAmount, PendingClaimRow, CategoryAmount, TopClaimant } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import Columns from '../charts/Columns';
import { compactMoney, formatPeriodLabel } from '../charts/axisPadding';
import TableView from '../charts/TableView';
import HBars from '../charts/HBars';
import { fmtMoney, CURRENCY_SYMBOL, previousMonthEntry } from './format';
import { formatMoney } from '../../../utils/format';

const DASHBOARD_PERMISSION = PERMISSIONS.DASHBOARD_VIEW;

const loadExpense = (institutionId: string): Promise<ExpenseSummary> =>
    DashboardService.getExpenseSummary(institutionId);

const ExpenseWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadExpense, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // A real zero (e.g. 500 historical PAID claims but nothing pending/approved this month) is a
    // genuine figure, not an absence of data — render it as a zero rather than collapsing it into
    // an "empty state" claim that isn't true.
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label="Pending claims" value={d.pendingCount} sub={formatMoney(d.pendingAmount, 'BDT')} tone={d.pendingCount > 0 ? 'warning' : 'default'} />
            {/* Approved spend is money leaving the business — never success-green. It was toned
                'good' purely for being non-zero, which read as "more spend is better". */}
            <StatTile label="Approved MTD" unit={CURRENCY_SYMBOL} value={fmtMoney(d.mtdApprovedTotal)} />
        </Stack>
    );
};

const loadExpenseTrend = (institutionId: string): Promise<MonthlyAmount[]> =>
    DashboardService.getExpenseTrend(institutionId);

const ExpenseTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadExpenseTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No approved claims yet — approvals will chart here by month.</Typography>;
    }
    return <Columns data={d.map(m => ({ label: m.month, value: m.amount }))} yFmt={fmtMoney} tickFmt={compactMoney} />;
};

// Previous calendar month's approved claim spend (Approved MTD reads zero on the 1st).
const ExpenseLastMonthWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadExpenseTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    const prev = previousMonthEntry(d);
    if (!prev) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No previous month to show yet.</Typography>;
    }
    return (
        <Stack spacing={0.5}>
            <StatTile label="Approved · last month" unit={CURRENCY_SYMBOL} value={fmtMoney(prev.amount)} />
            <Typography variant="caption" color="text.secondary">{formatPeriodLabel(prev.month)} · approved claims</Typography>
        </Stack>
    );
};

const loadQueue = (institutionId: string): Promise<PendingClaimRow[]> =>
    DashboardService.getExpenseQueue(institutionId);

const ExpenseQueueWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadQueue, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No claims waiting for approval.</Typography>;
    }
    return (
        <TableView
            columns={['Claimant', 'Claim', 'Amount', 'Waiting']}
            rows={d.map(r => [r.claimant, r.title, fmtMoney(r.amount), `${r.waitingDays}d`])}
        />
    );
};

const loadByCategory = (institutionId: string): Promise<CategoryAmount[]> =>
    DashboardService.getExpenseByCategory(institutionId);

const ExpenseByCategoryWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadByCategory, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No approved claims this month.</Typography>;
    }
    return (
        <HBars
            data={d.map(cat => ({
                label: cat.category,
                value: cat.amount,
                tooltip: [{ label: 'Approved', value: fmtMoney(cat.amount) }],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadTopClaimants = (institutionId: string): Promise<TopClaimant[]> =>
    DashboardService.getExpenseTopClaimants(institutionId);

const ExpenseTopClaimantsWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadTopClaimants, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No approved claims in the last 90 days.</Typography>;
    }
    return (
        <Stack spacing={0.5}>
            <HBars
                data={d.map(cl => ({
                    label: cl.claimant,
                    value: cl.amount,
                    tooltip: [
                        { label: 'Approved', value: fmtMoney(cl.amount) },
                        { label: 'Claims', value: String(cl.claimCount) },
                    ],
                }))}
                vFmt={fmtMoney}
            />
            <Typography variant="caption" color="text.secondary">Last 90 days</Typography>
        </Stack>
    );
};

export const expenseWidgets: WidgetDef[] = [
    { id: 'expense.summary', module: 'expense', title: 'Expense — pending & approved', subtitle: 'Claims awaiting approval and spend approved this month', permission: DASHBOARD_PERMISSION, size: 'md', component: ExpenseWidget },
    { id: 'expense.trend', module: 'expense', title: 'Expense — approved by month', subtitle: 'Approved claim value per month, last 6 months', permission: DASHBOARD_PERMISSION, size: 'lg', component: ExpenseTrendWidget },
    { id: 'expense.lastMonth', module: 'expense', title: 'Expense — approved last month', subtitle: 'Approved claim spend for the previous calendar month', permission: DASHBOARD_PERMISSION, size: 'md', component: ExpenseLastMonthWidget },
    { id: 'expense.queue', module: 'expense', title: 'Expense — approval queue', subtitle: 'Claims waiting for approval, oldest first', permission: DASHBOARD_PERMISSION, size: 'lg', component: ExpenseQueueWidget },
    { id: 'expense.byCategory', module: 'expense', title: 'Expense — this month by category', subtitle: 'Approved claim spend this month by category', permission: DASHBOARD_PERMISSION, size: 'md', component: ExpenseByCategoryWidget },
    { id: 'expense.topClaimants', module: 'expense', title: 'Expense — top claimants', subtitle: 'Employees with the most approved claims, last 90 days', permission: DASHBOARD_PERMISSION, size: 'md', component: ExpenseTopClaimantsWidget },
];
